import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Paper,
  Tabs,
  Tab,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArgonBox from 'components/ArgonBox'; // Assuming ArgonBox is a styled Box
import { NEW_OBJECT_TEMPLATE } from 'config/ootbConfig'; // Adjust path as needed
import ArgonTypography from 'components/ArgonTypography';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80vw',
  maxWidth: '1200px',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 0, // Padding will be handled by Paper content
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px', // Match Argon design
};

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`config-tabpanel-${index}`}
      aria-labelledby={`config-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function GlobalConfigEditor({ open, onClose, initialConfig, onSave, isLoading }) {
  const [activeTab, setActiveTab] = useState(0);
  const [currentConfig, setCurrentConfig] = useState({ cpq: [], rc: [] });

  useEffect(() => {
    // Deep copy initialConfig to prevent direct mutation
    setCurrentConfig({
      cpq: initialConfig.cpq ? initialConfig.cpq.map((item) => ({ ...item })) : [],
      rc: initialConfig.rc ? initialConfig.rc.map((item) => ({ ...item })) : [],
    });
  }, [initialConfig, open]); // Reset when opened or initialConfig changes

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleInputChange = (tabKey, rowIndex, fieldName, value) => {
    setCurrentConfig((prevConfig) => {
      const updatedTabObjects = [...prevConfig[tabKey]];
      updatedTabObjects[rowIndex] = {
        ...updatedTabObjects[rowIndex],
        [fieldName]: value,
      };
      return { ...prevConfig, [tabKey]: updatedTabObjects };
    });
  };

  const handleAddObject = (tabKey) => {
    setCurrentConfig((prevConfig) => ({
      ...prevConfig,
      [tabKey]: [...prevConfig[tabKey], { ...NEW_OBJECT_TEMPLATE, _tempId: Date.now() }], // Add a temp key for React list
    }));
  };

  const handleRemoveObject = (tabKey, rowIndex) => {
    setCurrentConfig((prevConfig) => {
      const updatedTabObjects = [...prevConfig[tabKey]];
      updatedTabObjects.splice(rowIndex, 1);
      return { ...prevConfig, [tabKey]: updatedTabObjects };
    });
  };

  const renderTable = (tabKey, objects) => (
    <TableContainer component={Paper} elevation={2} sx={{ mb: 2 }}>
      <Table stickyHeader>
        <TableHead sx={{ display: 'contents' }}>
          <TableRow>
            <TableCell>
              <ArgonTypography variant="caption" fontWeight="medium">
                Object Name
              </ArgonTypography>
            </TableCell>

            <TableCell>
              <ArgonTypography variant="caption" fontWeight="medium">
                ID Field List
              </ArgonTypography>
            </TableCell>
            <TableCell>
              <ArgonTypography variant="caption" fontWeight="medium">
                Additional Field List
              </ArgonTypography>
            </TableCell>
            <TableCell>
              <ArgonTypography variant="caption" fontWeight="medium">
                Display Columns
              </ArgonTypography>
            </TableCell>
            <TableCell>
              <ArgonTypography variant="caption" fontWeight="medium">
                Actions
              </ArgonTypography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {objects.map((obj, rowIndex) => (
            <TableRow key={obj._tempId || rowIndex}>
              {' '}
              {/* Use tempId if available, else index */}
              <TableCell>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={obj.objectName}
                  onChange={(e) => handleInputChange(tabKey, rowIndex, 'objectName', e.target.value)}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={obj.idFields}
                  onChange={(e) => handleInputChange(tabKey, rowIndex, 'idFields', e.target.value)}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={obj.additionalFields}
                  onChange={(e) => handleInputChange(tabKey, rowIndex, 'additionalFields', e.target.value)}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={obj.displayColumns}
                  onChange={(e) => handleInputChange(tabKey, rowIndex, 'displayColumns', e.target.value)}
                />
              </TableCell>
              <TableCell>
                <IconButton onClick={() => handleRemoveObject(tabKey, rowIndex)} size="small" color="error">
                  <CloseIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const handleSaveClick = () => {
    // Remove temporary IDs before saving
    const configToSave = {
      cpq: currentConfig.cpq.map(({ _tempId, ...rest }) => rest),
      rc: currentConfig.rc.map(({ _tempId, ...rest }) => rest),
    };
    onSave(configToSave);
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="global-config-editor-title">
      <Paper sx={modalStyle}>
        <ArgonBox
          bgColor="primary.main" // Or your theme's header color
          color="white"
          p={2}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
          }}
        >
          <Typography variant="h6" component="h2" id="global-config-editor-title">
            Global Configuration
          </Typography>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </ArgonBox>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mx: 2, mt: 1 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="configuration tabs">
            <Tab label="CPQ Objects" id="config-tab-0" aria-controls="config-tabpanel-0" />
            <Tab label="RC Objects" id="config-tab-1" aria-controls="config-tabpanel-1" />
          </Tabs>
        </Box>
        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'row-reverse' }}>
              <Button
                variant="contained"
                color="info"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => handleAddObject('cpq')}
                sx={{ mb: 2 }}
              >
                Add CPQ Object
              </Button>
            </Box>
            {renderTable('cpq', currentConfig.cpq)}
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'row-reverse' }}>
              <Button
                variant="contained"
                color="info"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => handleAddObject('rc')}
                sx={{ mb: 2 }}
              >
                Add RC Object
              </Button>
            </Box>
            {renderTable('rc', currentConfig.rc)}
          </TabPanel>
        </Box>
        <ArgonBox sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', textAlign: 'right' }}>
          <Button onClick={onClose} sx={{ mr: 1 }} color="secondary">
            Cancel
          </Button>
          <Button variant="contained" color="info" onClick={handleSaveClick} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save Configuration'}
          </Button>
        </ArgonBox>
      </Paper>
    </Modal>
  );
}

GlobalConfigEditor.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialConfig: PropTypes.shape({
    cpq: PropTypes.arrayOf(PropTypes.object),
    rc: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

GlobalConfigEditor.defaultProps = {
  isLoading: false,
};

export default GlobalConfigEditor;
